import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'
import ChatSearch from './ChatSearch'
import ChatBody from './ChatBody'
import Typography from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import { withStyles } from 'beam/dist/styles'
import { grey, lightBlue } from '@mui/material/colors'
import { ColorScheme } from './chat.constants'

import { authenticatedUserShape, withAuthenticatedUser } from '../../../Permissions/'

const styles = () => ({
  root: {
    display: 'inline-flex',
    flexDirection: 'column',
    height: '100%',
  },
  rootNoBubbles: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
})

const CLUMP_TYPE = {
  HIT: 'HIT',
  QUERY: 'QUERY',
  NONE: 'NONE',
}

class ChatContent extends Component {

  state = {
    query: '',
    queryTotalHits: 0,
    queryCurrentIndex: 0,
    bubbles: [],
  }

  componentDidMount() {
    // Call API to get data
    this.requestChat()
    this.searchFor({ target: { value: '' } })
  }

  componentDidUpdate(prevProps) {

    const { ccrid, loading, data: { chatBody } } = this.props

    // Request new chat if the ccrId changed, otherwise search for text
    if (prevProps.ccrid !== ccrid) {
      this.requestChat()
    } else if (!loading && (prevProps.data.chatBody !== chatBody)) {
      this.searchFor({ target: { value: this.state.query ? this.state.query : '' } })
    }
  }

  requestChat() {
    const {
      requestChat,
      ccrid,
      goalId,
      hasReviewGoal,
      auditTextViewed,
      completeReviewGoal,
      authenticatedUser: { user },
    } = this.props
    requestChat(ccrid)
    auditTextViewed(ccrid)
    if (hasReviewGoal) {
      completeReviewGoal(ccrid, goalId, user.id)
    }
  }

  renderLoading() {
    return <CircularProgress />
  }

  renderError() {
    const { t } = this.props
    return (
      <div>
        <Typography color="secondary" align="center">
          {t('error')}
        </Typography>
        <Typography color="secondary" align="center">
          {t('textDataLoadError')}
        </Typography>
      </div>
    )
  }

  goToQueryHit = direction => {
    let nextIndex = this.state.queryCurrentIndex + direction
    if (nextIndex < 1) {
      nextIndex = this.state.queryTotalHits
    } else if (nextIndex > this.state.queryTotalHits) {
      nextIndex = 1
    }
    this.setState({
      queryCurrentIndex: nextIndex,
    })
  }

  // In addition to generating individual messages; provides ability to search for text in the chat. Highlights the text that matches the search query.
  searchFor = ({ target: { value: query } }) => {

    let queryTotalHits = 0
    let hitCount = 0

    const { agent: selectedAgent, orgAgents, data: { chatBody } } = this.props

    const messages = chatBody ? chatBody.messages : []
    const participants = chatBody ? chatBody.participants : []

    const defaultAgentColor = ColorScheme.agent_default_color_tinted
    const agentColors = [...ColorScheme.agent_colors_tinted]

    // assign each participant a color
    const participantColors = new Array(participants.length)
    participants.forEach(participant => {
      const isSelectedAgent = selectedAgent.agentAcdId === participant.externalId
      if (isSelectedAgent) {
        participantColors[participant.id] = lightBlue[800]                // selected agent
      } else if (participant.role !== 'agent') {
        participantColors[participant.id] = grey['300']                   // non agent
      } else {
        const color = agentColors.shift()                                 // remove from list so not available anymore
        if (color) {
          participantColors[participant.id] = color                       // secondary agent
        } else {
          participantColors[participant.id] = defaultAgentColor           // no more agent colors available, use default
        }
      }
    })

    // Get beginning and end index of each query hit
    const bubbles = messages.map(message => {

      // number of hits on this message
      if (query !== '') {
        queryTotalHits += message.body.toLowerCase().split(query.toLowerCase()).length - 1
      }

      // Creates an array of the same length as the message body, with each index containing the index of the clump it belongs to
      const textToClumpsMap = Array(message.body.length).fill(-1)

      // ASH TODO - get this from API results when we support phrase hits
      const hits = []

      const participant = participants.find(p => p.id === message.authorId)

      // Author label follows the folloing hierarchy, depedning on the data available:
      // agent display name, message label attribute, author external ID, participant numeric index.
      // Participant will be null only if we have bad data. Add defensive code so we do not blow up.
      const authorLabel = orgAgents.find(a => a.acdId === participant?.externalId)?.name
        || participant?.label
        || participant?.externalId
        || (participant?.id && `Participant ${participant?.id}`)
        || 'Unknown Message Author'

      const isSelectedAgent = participant ? selectedAgent.agentAcdId === participant.externalId : false

      // Create a bubble for each message
      return {
        start: message.sendTimeMs,
        authorLabel,
        isSelectedAgent,
        id: message.id,
        color: participant ? participantColors[participant.id] : grey['300'],                     // defensive code for bad data

        // Based on similar logic as transcription
        // Creates clumps for each message.
        // Creates an array with clumps. We want the hits to be handled last so we can show the hits on top of phrase hits. So we order the array with phrase hits followed by query hits
        // Creates an array which maps each index of the message body to the clump it belongs to
        // Then goes through each index of the message body and creates clumps based on the clump map with the text and type of clump for formatting

        clumps: (({ body }) => {

          // Sort by shortest first
          const sortedTranscriptionHits = [...hits]
            .sort((a, b) => -1 * (a.text.end - a.text.start) - (b.text.end - b.text.start))
            .map(hit => ({ ...hit, type: CLUMP_TYPE.HIT }))

          // construct matches from query
          const queryHits = []
          if (query !== '') {
            let lastQueryIndex = 0
            // Until you can't find any matches
            while (body.toLowerCase().indexOf(query.toLowerCase(), lastQueryIndex) !== -1) {
              // found a match
              const queryIndex = body.toLowerCase().indexOf(query.toLowerCase(), lastQueryIndex)
              hitCount++
              // add the hit to the queryHits array
              queryHits.push({
                type: CLUMP_TYPE.QUERY,
                words: query,
                text: {
                  start: queryIndex,
                  end: queryIndex + query.length - 1,
                },
                qHit: true,
                qIndex: hitCount,
              })
              lastQueryIndex = queryIndex + query.length
            }
          }

          // Combine transcription and query hits, query hits are last, so they overwrite everything
          const sortedClumps = [...sortedTranscriptionHits, ...queryHits]

          // Map each hit onto the textToClumpsMap (longest will naturally overwrite shortest)
          sortedClumps.forEach(({ text: { start, end } }, sortedClumpIndex) => {
            for (let i = start; i <= end; i++) {
              textToClumpsMap[i] = sortedClumpIndex
            }
          })

          // Generate clumps for this message
          // We have all the clumps in the sortedClumps array
          // We have the map of which clump each index of the text belongs to in the textToClumpsMap
          const clumps = []
          let currentIndex = 0

          // Iterate through the entire text
          while (currentIndex < body.length) {

            // Set the current clump
            const currentClumpIndex = textToClumpsMap[currentIndex]
            const currentClump = sortedClumps[currentClumpIndex]
            const clumpStartIndex = currentIndex                      // index where the clump starts

            // Find the end of the clump; while incrementing the current index
            while (textToClumpsMap[currentIndex] === currentClumpIndex) {
              currentIndex++                                          // index where the clump ends
            }

            // Create the clump
            clumps.push({
              words: body.substring(clumpStartIndex, currentIndex),
              hit: !!(currentClump && currentClump.type === CLUMP_TYPE.HIT),
              qHit: !!(currentClump && currentClump.type === CLUMP_TYPE.QUERY),
              qIndex: currentClump && currentClump.type === CLUMP_TYPE.QUERY ? currentClump.qIndex : -1,
            })
          }

          return clumps
        })(message),
      }
    })

    const queryCurrentIndex = queryTotalHits !== 0 ? 1 : 0

    // Update the state
    this.setState({
      query,
      queryTotalHits,
      queryCurrentIndex,
      bubbles,
    })
  }

  renderData() {
    const {
      printRef,
      isSearchOpen,
      closeSearch,
      contactTz,
      contactType,
    } = this.props

    const { query, queryCurrentIndex, queryTotalHits, bubbles } = this.state

    return (
      <Fragment>
        <ChatSearch
          searchFor={this.searchFor}
          query={query}
          queryCurrentIndex={queryCurrentIndex}
          queryTotalHits={queryTotalHits}
          goToQueryHit={this.goToQueryHit}
          open={isSearchOpen}
          onClose={closeSearch}
        />
        <ChatBody
          ref={printRef}
          bubbles={bubbles}
          queryCurrentIndex={queryCurrentIndex}
          contactTz={contactTz}
          contactType={contactType}
        />
      </Fragment>
    )
  }

  render() {
    const { classes, loading, error } = this.props
    const noBubbles = loading || error

    return (
      <div className={classNames(classes.root, noBubbles && classes.rootNoBubbles)}>
        {(loading && this.renderLoading()) ||
          (error && this.renderError()) ||
          this.renderData()}
      </div>
    )
  }
}

ChatContent.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  /** if the transcription data is still being loaded */
  loading: PropTypes.bool.isRequired,
  /** if loading the transcription data failed for some reason */
  error: PropTypes.bool.isRequired,
  /** type of Contact, should only be CHAT */
  contactType: PropTypes.string.isRequired,
  /** id of call to load */
  ccrid: PropTypes.number,
  /** Result from the API */
  data: PropTypes.shape({
    chatBody: PropTypes.object,
    hits: PropTypes.array,
  }),
  requestChat: PropTypes.func,
  authenticatedUser: authenticatedUserShape.isRequired,
  isSearchOpen: PropTypes.bool,
  closeSearch: PropTypes.func,
  orgAgents: PropTypes.array,
  printRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  agent: PropTypes.object,
  auditTextViewed: PropTypes.func.isRequired,
  completeReviewGoal: PropTypes.func.isRequired,
  goalId: PropTypes.number,
  hasReviewGoal: PropTypes.bool.isRequired,
  contactTz: PropTypes.string.isRequired,
}

export { ChatContent as UnstyledChatContent }
export default withStyles(styles)(withTranslation()(withAuthenticatedUser(ChatContent)))
